import {Link} from '../../sidebar/sidebar-menu/types'

/** One crumb of the trail. `path` is absent only for section labels (groups link to their first child). */
export type TrailCrumb = {
  /** Raw sidebar title — usually an i18n key, sometimes literal text. */
  title: string
  path?: string
}

/** Route part of a sidebar `to` — no query string or hash, no trailing slash. */
const routeOf = (to: string): string => {
  const clean = to.split(/[?#]/)[0]
  return clean.length > 1 ? clean.replace(/\/+$/, '') : clean
}

const isMatch = (route: string, pathname: string): boolean =>
  !!route && route.startsWith('/') && (pathname === route || pathname.startsWith(route + '/'))

const isNavigable = (node: Link) => node.visible !== false && !node.external

/**
 * Route a crumb should link to: the node's own, or — for groups, which have
 * `to: ''` — the first visible descendant that has one.
 */
const linkTarget = (node: Link): string | undefined => {
  const own = routeOf(node.to ?? '')
  if (own.startsWith('/')) return own
  for (const child of node.subLinks ?? []) {
    if (!isNavigable(child)) continue
    const found = linkTarget(child)
    if (found) return found
  }
  return undefined
}

export type MenuTrail = {
  /** Section label, then each group, then the matching item. */
  chain: TrailCrumb[]
  /** True when `pathname` is the item's own route, false when the page is deeper than the sidebar goes. */
  exact: boolean
}

/**
 * Finds where `pathname` lives in the sidebar tree and returns the chain of
 * crumbs leading to it: section label, then each group, then the matching
 * item. The deepest link whose route is a prefix of `pathname` wins, so
 * `/institute/vendors/add` picks the "Add Vendor" item over the "Vendors"
 * item at `/institute/vendors`.
 *
 * Hidden (`visible === false`) and external links are skipped along with
 * their subtrees. Returns `null` when nothing in the tree matches.
 */
export const resolveMenuTrail = (links: Link[], pathname: string): MenuTrail | null => {
  const path = routeOf(pathname)
  let best: {chain: TrailCrumb[]; length: number; exact: boolean} | null = null

  const visit = (node: Link, ancestors: TrailCrumb[]) => {
    if (!isNavigable(node)) return
    const route = routeOf(node.to ?? '')
    const self: TrailCrumb = {title: node.title, path: linkTarget(node)}
    if (isMatch(route, path) && (!best || route.length > best.length)) {
      best = {chain: [...ancestors, self], length: route.length, exact: route === path}
    }
    node.subLinks?.forEach((child) => visit(child, [...ancestors, self]))
  }

  let section: TrailCrumb | null = null
  for (const link of links) {
    if (link.type === 'label') {
      section = link.visible === false ? null : {title: link.title}
      continue
    }
    visit(link, section ? [section] : [])
  }

  if (!best) return null
  const {chain, exact} = best as {chain: TrailCrumb[]; exact: boolean}
  return {chain, exact}
}

/** Collapses runs of identical titles — a group and its first item often share one. */
export const dedupeConsecutive = <T extends {title: string}>(crumbs: T[]): T[] =>
  crumbs.filter((crumb, i) => i === 0 || crumb.title !== crumbs[i - 1].title)
